import React, {memo, useCallback, useMemo, useState} from "react"
import {PasswordContainer} from "../../../../components/PasswordContainer"
import {SettingsPageContainer} from "../../../../components/Settings/SettingsPageContainer"
import {useKeyboard} from "../../../../hooks/keyboard"
import {SettingInputComponent} from "../../../../components/Settings/SettingInputComponent"
import {useLocalization} from "../../../../hooks/localization"
import {useHistory, useParams} from "react-router-dom"
import {useSetSelector} from "../../../../hooks/set-selector"
import {useRPC} from "../../../../hooks/rpc"
import {SIPFormatType} from "../../../../store/control/state"
import {useMessage} from "../../../../hooks/message"
import {useQuestion} from "../../../../hooks/question"

const BACK_URL = "/developer/sip-format"
export const SIPFormatEditScreen = memo(function SIPFormatEditScreen() {
    const {t} = useLocalization()
    const [rpc] = useRPC()
    const formatListSet = useSetSelector(state => state.control.sipFormatTypeList)
    const {failure, success} = useMessage()
    const question = useQuestion()
    const history = useHistory()
    const params = useParams<{id: string}>()
    const [setActiveInput, renderKeyboard, currentInput] = useKeyboard()
    const [setActiveNumpadInput, renderNumpadKeyboard, currentNumpadInput] = useKeyboard("numpad", true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const selected = useMemo(() => formatListSet[params?.id] || null, [])
    const [name, setName] = useState((selected?.id != null && selected?.name) || "")
    const [format, setFormat] = useState((selected?.id != null && selected?.format) || "")
    const [formatLength, setFormatLength] = useState((selected?.id != null && selected?.format_lenght.toString()) || "")

    const onBlur = useCallback(() => {
        setActiveInput(null)
        setActiveNumpadInput(null)
    }, [setActiveInput, setActiveNumpadInput])

    const onSavePressed = useCallback(() => {
        if (name == null || name.length === 0) {
            failure(t("pls_enter_a_name"))
            return false
        }
        if (format == null || format.length === 0) {
            failure(t("pls_enter_a_format"))
            return false
        }
        if (formatLength == null || formatLength.length === 0) {
            failure(t("pls_enter_a_valid_format_length"))
            return false
        }
        const sipFormat: SIPFormatType = {
            id: selected?.id || null,
            name,
            format,
            format_lenght: Number(formatLength)
        }
        rpc.updateSipFormat(sipFormat)
            .then(() => {
                success(t("successfully_saved"))
                history.push(BACK_URL)
            })
            .catch(failure)
    }, [t, failure, success, history, rpc, name, selected, format, formatLength])

    const onDeletePressed = useCallback(() => {
        return question(t("are_you_sure"), () => {
            rpc.deleteSipFormat(selected?.id)
                .then(() => {
                    success(t("successfully_deleted"))
                    history.push(BACK_URL)
                })
                .catch(failure)
        })
    }, [t, question, failure, success, history, rpc, selected])

    const buttons = useMemo(
        () => [
            {
                text: t("delete"),
                onClick: onDeletePressed,
                className: "red",
                hidden: !(selected?.id != null)
            },
            {
                text: t("save"),
                onClick: onSavePressed,
                className: "green"
            }
        ],
        [t, onSavePressed, onDeletePressed, selected]
    )

    return (
        <PasswordContainer requiredRole="developer" isFullscreen={true}>
            <SettingsPageContainer
                buttons={buttons}
                className="service-edit"
                backUrl={BACK_URL}
                currentInput={currentInput}
                currentNumpadInput={currentNumpadInput}
            >
                <div className="content-divider">
                    <div className="content-wrapper">
                        <SettingInputComponent
                            placeholder={t("name")}
                            label={t("name")}
                            value={name}
                            setValue={setName}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}
                        />
                        <SettingInputComponent
                            placeholder={t("write_a_format")}
                            label={t("format")}
                            value={format}
                            setValue={setFormat}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}
                        />
                        <SettingInputComponent
                            placeholder={t("write_format_length")}
                            label={t("format_length")}
                            value={formatLength}
                            setValue={setFormatLength}
                            setActiveInput={setActiveNumpadInput}
                            onBlur={onBlur}
                        />
                    </div>
                    <div className="content-wrapper"></div>
                </div>
            </SettingsPageContainer>
            {renderKeyboard()}
            {renderNumpadKeyboard()}
        </PasswordContainer>
    )
})
